// Image Library Picker — modal para reutilizar imágenes ya subidas y procesadas
// con Nano Banana, evitando volver a procesar (ahorro de recursos de IA).
//
// Props:
//   open       : bool — controla visibilidad
//   onClose    : () => void
//   onPick     : (img) => void — recibe el doc seleccionado del repositorio
//                ({id, url, url_processed, url_original, name, ...})
//   onUpload?  : opcional — si el padre quiere también ofrecer "subir nueva"
//                fuera del picker. (No usado por defecto.)
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Search, Upload, Trash2, Library, Image as ImageIcon, Loader2, RefreshCw } from "lucide-react";

const DOMAINS = [
  "comprension_lectora", "razonamiento_logico", "aritmetica",
  "geometria", "algebra", "fisica", "estadistica",
];

const GRADES = [
  "2-primaria", "3-primaria", "4-primaria", "5-primaria", "6-primaria",
  "1-basica", "2-basica", "3-basica", "media-1", "media-2", "media-3",
];

export default function ImageLibraryPicker({ open, onClose, onPick }) {
  const [tab, setTab] = useState("browse"); // browse | upload
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState("");
  const [grade, setGrade] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (domain) params.domain = domain;
      if (grade) params.grade = grade;
      const { data } = await api.get("/image-library", { params });
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error cargando repositorio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && tab === "browse") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const handlePick = async (img) => {
    try {
      await api.post(`/image-library/${img.id}/increment-use`);
    } catch (_e) { /* silencio: no es crítico */ }
    onPick?.(img);
    onClose?.();
  };

  const handleDelete = async (img) => {
    if (!confirm(`¿Eliminar "${img.name}" del repositorio? Esta acción es permanente.`)) return;
    try {
      await api.delete(`/image-library/${img.id}`);
      toast.success("Imagen eliminada");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      // Si está en uso, ofrecer forzado al Super Admin
      if (/En uso/i.test(String(detail))) {
        if (confirm(`${detail}\n\n¿FORZAR eliminación y limpiar referencias en las preguntas que la usen? Esta acción es irreversible (requiere Super Admin).`)) {
          try {
            const { data } = await api.delete(`/image-library/${img.id}?force=true`);
            toast.success(`Imagen eliminada${data.templates_updated ? ` · referencias limpiadas en ${data.templates_updated} plantilla(s)` : ""}`);
            load();
            return;
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al forzar");
            return;
          }
        }
      }
      toast.error(formatApiErrorDetail(detail) || "Error");
    }
  };

  const handleCleanupUnused = async () => {
    const unusedCount = items.filter((i) => !i.uses_count).length;
    if (unusedCount === 0) {
      toast.info("No hay imágenes en desuso para eliminar.");
      return;
    }
    if (!confirm(`Se eliminarán ${unusedCount} imagen(es) en desuso (sin asignar a ninguna pregunta).\n\n¿Continuar?`)) return;
    try {
      const { data } = await api.post("/image-library/admin/cleanup-unused");
      toast.success(`Imágenes en desuso eliminadas: ${data.deleted}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar");
    }
  };

  const handleUpload = async (form, file) => {
    if (!file) { toast.error("Selecciona un archivo"); return; }
    if (!form.name || form.name.trim().length < 2) { toast.error("El nombre es requerido (mín. 2)"); return; }
    if (file.size < 1024) { toast.error("La imagen es demasiado pequeña (< 1 KB)"); return; }
    setUploading(true);
    let uploadOk = false;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = {
        name: form.name.trim(),
        tags: form.tags || "",
        process_with_ai: form.ai_mode !== "none" ? "true" : "false",
        ai_mode: form.ai_mode === "colorize" ? "colorize" : "clean",
      };
      if (form.domain) params.domain = form.domain;
      if (form.grade) params.grade = form.grade;
      const { data } = await api.post("/image-library/upload", fd, { params });
      uploadOk = true;
      toast.success(
        data.image_processing
          ? `Imagen subida ✓ — procesando en background (modo "${form.ai_mode === "colorize" ? "Colorear" : "Limpiar"}")`
          : "Imagen subida al repositorio ✓",
      );
      setTab("browse");
      // Refresh independiente — un fallo aquí NO indica que el upload falló.
      try { await load(); } catch {
        toast.message("La imagen se guardó. Refresca el repositorio en unos segundos para verla.");
      }
    } catch (e) {
      if (!uploadOk) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error subiendo");
      }
    } finally {
      setUploading(false);
    }
  };

  // Fix 2.1.3: Siempre renderizar el portal. El contenido se muestra
  // condicionalmente para que React pueda limpiar nodos hijo correctamente
  // al cerrar, sin crash removeChild.
  return createPortal((
    <>
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[55] bg-[#1F2547]/30 backdrop-blur-[2px] md:right-[480px]"
            onClick={onClose}
            data-testid="image-library-backdrop"
          />
      {/* Panel slide-over anclado a la derecha */}
      <aside
        className="fixed top-0 right-0 z-[60] h-full w-full md:w-[480px] bg-white shadow-2xl border-l-2 border-[#E1E5F0] flex flex-col animate-slide-in-right"
        data-testid="image-library-modal"
        role="dialog"
        aria-label="Repositorio de imágenes"
      >
        <header className="px-5 py-4 border-b border-[#E1E5F0] flex items-center justify-between gap-3 shrink-0">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790] flex items-center gap-1.5">
              <Library size={12} /> Repositorio de imágenes
            </p>
            <h3 className="font-display text-base font-extrabold text-[#1F2547] leading-tight">
              Reutiliza imágenes ya procesadas
            </h3>
            <p className="text-[10px] text-[#6E7591]">Reutiliza una imagen en múltiples preguntas.</p>
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]" data-testid="image-library-close">
            <X size={18} />
          </button>
        </header>

        <div className="px-5 pt-3 border-b border-[#E1E5F0] flex gap-2 shrink-0">
          <TabBtn active={tab === "browse"} onClick={() => setTab("browse")} testId="image-library-tab-browse">
            <Library size={12} /> Explorar ({items.length})
          </TabBtn>
          <TabBtn active={tab === "upload"} onClick={() => setTab("upload")} testId="image-library-tab-upload">
            <Upload size={12} /> Subir nueva
          </TabBtn>
        </div>

        <div className="flex-1 overflow-y-auto">
          {tab === "browse" && (
            <BrowseTab
              items={items} loading={loading}
              q={q} setQ={setQ}
              domain={domain} setDomain={setDomain}
              grade={grade} setGrade={setGrade}
              onSearch={onSearch}
              onPick={handlePick}
              onDelete={handleDelete}
              onCleanupUnused={handleCleanupUnused}
              onReload={load}
            />
          )}

          {tab === "upload" && (
            <UploadTab onUpload={handleUpload} uploading={uploading} />
          )}
        </div>
      </aside>
        </>
      )}
    </>
  ), document.body);
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`px-3 py-2 text-xs font-extrabold rounded-t-lg inline-flex items-center gap-1.5 transition-colors ${
        active ? "bg-[#1F2547] text-white" : "text-[#6E7591] hover:text-[#1F2547]"
      }`}>
      {children}
    </button>
  );
}

function BrowseTab({ items, loading, q, setQ, domain, setDomain, grade, setGrade, onSearch, onPick, onDelete, onCleanupUnused, onReload }) {
  const brokenCount = items.filter((x) => x.broken).length;
  const unusedCount = items.filter((x) => !x.uses_count).length;
  const purgeBroken = async () => {
    if (!confirm(`¿Eliminar ${brokenCount} archivo(s) perdido(s) sin uso del repositorio?`)) return;
    try {
      const { data } = await api.post("/image-library/admin/purge-broken");
      toast.success(`Limpiados: ${data.deleted} · Conservados en uso: ${data.kept_in_use}`);
      onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };
  return (
    <div className="px-5 py-4 space-y-3">
      <form onSubmit={onSearch} className="flex flex-wrap gap-2 items-center" data-testid="image-library-filters">
        <div className="flex-1 min-w-[140px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7591]" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o tag…"
            className="w-full pl-9 pr-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] text-sm"
            data-testid="image-library-search" />
        </div>
        <select value={domain} onChange={(e) => setDomain(e.target.value)}
          className="px-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-xs font-bold"
          data-testid="image-library-domain-filter">
          <option value="">Todos los dominios</option>
          {DOMAINS.map((d) => <option key={d} value={d}>{d.replace("_", " ")}</option>)}
        </select>
        <select value={grade} onChange={(e) => setGrade(e.target.value)}
          className="px-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-xs font-bold">
          <option value="">Todos los niveles</option>
          {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <button type="submit" className="jasq-btn-ghost text-xs inline-flex items-center gap-1" data-testid="image-library-search-btn">
          <Search size={12} /> Buscar
        </button>
        <button type="button" onClick={onReload} className="text-[#6E7591] hover:text-[#1F2547] p-2" title="Recargar">
          <RefreshCw size={14} />
        </button>
        {unusedCount > 0 && (
          <button
            type="button"
            onClick={onCleanupUnused}
            data-testid="image-library-cleanup-unused"
            title="Borrar todas las imágenes que no estén asignadas a ninguna pregunta"
            className="text-[11px] font-extrabold bg-[#A52258] text-white rounded-full px-3 py-1.5 hover:bg-[#1F2547] inline-flex items-center gap-1"
          >
            <Trash2 size={11} /> Eliminar {unusedCount} en desuso
          </button>
        )}
      </form>

      {brokenCount > 0 && (
        <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-[#FFE4E1] border-2 border-[#FFCCCC]" data-testid="image-library-broken-banner">
          <p className="text-[11px] text-[#A52258] font-bold">
            ⚠️ Detecté <b>{brokenCount}</b> imagen(es) con archivo perdido (residuo de deploys anteriores).
          </p>
          <button type="button" onClick={purgeBroken}
            data-testid="image-library-purge-broken"
            className="text-[11px] font-extrabold bg-[#A52258] text-white rounded-full px-3 py-1 hover:bg-[#1F2547]">
            Limpiar ahora
          </button>
        </div>
      )}

      <div className="min-h-[300px]" data-testid="image-library-grid">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#6E7591] gap-2 text-sm">
            <Loader2 size={16} className="animate-spin" /> Cargando…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-[#6E7591]">
            <ImageIcon size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm font-bold">El repositorio está vacío</p>
            <p className="text-xs mt-1">Cambia a "Subir nueva" para agregar la primera imagen.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {items.map((img) => (
              <ImageCard key={img.id} img={img} onPick={onPick} onDelete={onDelete} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ImageCard({ img, onPick, onDelete }) {
  const initialSrc = img.url || img.url_processed || img.url_original;
  const [imgSrc, setImgSrc] = useState(initialSrc);
  const isBroken = !!img.broken || !initialSrc;

  const handleError = () => {
    if (imgSrc !== img.url_original && img.url_original) {
      setImgSrc(img.url_original);
    } else if (imgSrc !== null) {
      setImgSrc(null); // Marca como rota y deja de pedirla
    }
  };

  return (
    <div className={`group border-2 rounded-xl overflow-hidden bg-white transition-colors ${isBroken ? "border-[#FFCCCC] opacity-70" : "border-[#E1E5F0] hover:border-[#A43790]"}`}
      data-testid={`image-library-item-${img.id}`}>
      <div className="aspect-square bg-[#F7F8FA] relative flex items-center justify-center">
        {(isBroken || !imgSrc) ? (
          <div className="text-center px-2">
            <ImageIcon size={28} className="mx-auto text-[#A6ACC1] opacity-50" />
            <p className="text-[9px] uppercase tracking-wider font-extrabold text-[#A52258] mt-1">Archivo perdido</p>
          </div>
        ) : (
          <img src={imgSrc} alt={img.name} onError={handleError}
            className="w-full h-full object-contain" />
        )}
        {img.image_processing && (
          <span className="absolute top-1 right-1 jasq-pill bg-[#FFF7DA] text-[#9C7A18] text-[9px] animate-pulse">
            Procesando…
          </span>
        )}
        {img.url_processed && !img.image_processing && !isBroken && (
          <span className="absolute top-1 left-1 jasq-pill bg-[#FFE9F0] text-[#A52258] text-[9px]">
            {img.ai_mode === "colorize" ? "🎨 Color" : "✨ Mejorada"}
          </span>
        )}
        {img.ai_error && !img.image_processing && !img.url_processed && !isBroken && (
          <span className="absolute top-1 left-1 jasq-pill bg-[#FFE4E1] text-[#A52258] text-[9px]" title={img.ai_error}>
            ⚠️ Error al procesar
          </span>
        )}
      </div>
      <div className="p-2 space-y-1.5">
        <p className="text-[11px] font-extrabold text-[#1F2547] truncate" title={img.name}>{img.name}</p>
        <div className="flex items-center gap-1.5 flex-wrap">
          {img.domain && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#F7F8FA] text-[#4C5478]">{img.domain}</span>}
          {img.grade && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#F7F8FA] text-[#4C5478]">{img.grade}</span>}
          {img.uses_count > 0 && (
            <span className="text-[9px] font-bold text-[#3FA84D]">×{img.uses_count}</span>
          )}
        </div>
        <div className="flex gap-1.5 pt-1">
          <button onClick={() => onPick(img)} disabled={isBroken}
            className="flex-1 text-[10px] font-extrabold bg-[#1F2547] text-white rounded px-2 py-1 hover:bg-[#A43790] disabled:opacity-40"
            data-testid={`image-library-pick-${img.id}`}>
            {isBroken ? "No disponible" : "Usar"}
          </button>
          <button onClick={() => onDelete(img)}
            title={img.uses_count > 0 ? "En uso · usa forzado para purgar (Super Admin)" : "Eliminar"}
            className="text-[#A52258] p-1 hover:bg-[#FFE9F0] rounded"
            data-testid={`image-library-delete-${img.id}`}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadTab({ onUpload, uploading }) {
  const [form, setForm] = useState({
    name: "", tags: "", domain: "", grade: "", ai_mode: "clean",
  });
  const [file, setFile] = useState(null);
  const fileURL = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  useEffect(() => () => { if (fileURL) URL.revokeObjectURL(fileURL); }, [fileURL]);

  const AI_MODES = [
    { id: "none",     label: "Sin procesar",   desc: "Tal cual." },
    { id: "clean",    label: "Limpiar",  desc: "Quita ruido. Mantiene blancos." },
    { id: "colorize", label: "Colorear", desc: "Rellena los blancos con colores." },
  ];

  return (
    <div className="px-5 py-4 space-y-3">
      <Field label="Nombre *">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder='Ej: "Galletas en bandeja - 12 unidades"'
          className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] text-sm"
          data-testid="image-library-upload-name" />
      </Field>
      <Field label="Tags (separados por comas)">
        <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
          placeholder="multiplicación, bandeja, 12"
          className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] text-sm"
          data-testid="image-library-upload-tags" />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Dominio">
          <select value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[#E1E5F0] text-xs font-bold">
            <option value="">—</option>
            {DOMAINS.map((d) => <option key={d} value={d}>{d.replace("_", " ")}</option>)}
          </select>
        </Field>
        <Field label="Nivel">
          <select value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })}
            className="w-full px-2 py-2 rounded-xl border-2 border-[#E1E5F0] text-xs font-bold">
            <option value="">—</option>
            {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Procesamiento de imagen">
        <div className="grid grid-cols-3 gap-1.5" data-testid="image-library-ai-modes">
          {AI_MODES.map((m) => (
            <button key={m.id} type="button" onClick={() => setForm({ ...form, ai_mode: m.id })}
              data-testid={`image-library-ai-${m.id}`}
              className={`text-left rounded-xl border-2 px-2 py-1.5 transition-colors ${
                form.ai_mode === m.id
                  ? "border-[#A43790] bg-[#FFF1F8]"
                  : "border-[#E1E5F0] bg-white hover:border-[#A43790]"
              }`}>
              <div className="text-[10px] font-extrabold text-[#1F2547]">
                {m.id === "colorize" ? "🎨 " : m.id === "clean" ? "✨ " : "⚪️ "}{m.label}
              </div>
              <div className="text-[9px] text-[#6E7591] leading-tight mt-0.5">{m.desc}</div>
            </button>
          ))}
        </div>
      </Field>

      <Field label="Archivo (PNG/JPG · mín. 64×64 px · máx 5 MB)">
        <label className="block border-2 border-dashed border-[#E1E5F0] hover:border-[#A43790] rounded-xl p-4 text-center cursor-pointer">
          {fileURL ? (
            <img src={fileURL} alt="preview" className="max-h-32 mx-auto mb-2 rounded" />
          ) : (
            <Upload size={20} className="mx-auto mb-2 text-[#6E7591]" />
          )}
          <span className="text-xs font-bold text-[#A43790]">
            {file ? file.name : "Click para seleccionar"}
          </span>
          <input type="file" accept="image/*" hidden
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="image-library-upload-file" />
        </label>
      </Field>
      <button onClick={() => onUpload(form, file)} disabled={uploading || !file || !form.name}
        className="w-full jasq-btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-40"
        data-testid="image-library-upload-submit">
        {uploading ? (<><Loader2 size={14} className="animate-spin" /> Subiendo…</>) : (<><Upload size={14} /> Subir al repositorio</>)}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-wider uppercase font-extrabold text-[#6E7591]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
